import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Undo2, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

/**
 * Surfaces the 7-day data-restore window on any page it's mounted on.
 * Hidden by default — calls `GET /api/auth/me/data/archive-status` on mount
 * and renders the emerald banner only if the backend reports
 * `has_archive: true`. Clicking Restore fires `POST /api/auth/me/data/restore`
 * which both replays the archive AND opens a support ticket on
 * gotafitness.com (handled server-side).
 *
 * Shared between `Account.jsx` and the Dashboard so a user who never
 * visits Account doesn't silently lose their data when the 7-day window
 * elapses.
 */
const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

async function authFetch(path, { method = "GET" } = {}) {
  const token = localStorage.getItem("ta_access_token");
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function DataRestoreBanner({ compact = false }) {
  const [archive, setArchive] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await authFetch("/api/auth/me/data/archive-status");
      setArchive(data?.has_archive ? data : null);
    } catch {
      setArchive(null);
    }
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!archive?.has_archive) return null;

  const days = archive.days_remaining;
  const timeLabel =
    days < 1
      ? "Less than 24 hours left"
      : `${days} day${days >= 2 ? "s" : ""} remaining`;

  const handleRestore = async () => {
    if (isRestoring) return;
    setIsRestoring(true);
    try {
      await authFetch("/api/auth/me/data/restore", { method: "POST" });
      toast({
        title: "Data restored",
        description:
          "We've reopened a support ticket so the team can confirm reactivation. Reloading…",
      });
      setArchive(null);
      // Hard reload so every cached query refetches against the restored data.
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast({
        title: "Couldn't restore",
        description: err.message || "The 7-day window may have elapsed.",
        variant: "destructive",
      });
      setIsRestoring(false);
    }
  };

  return (
    <Card
      className={`border-emerald-400/40 bg-emerald-500/[0.08] flex flex-col sm:flex-row sm:items-center gap-3 ${
        compact ? "p-3" : "p-4"
      }`}
      data-testid="archive-restore-banner"
    >
      <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 flex items-center justify-center shrink-0">
        <Clock className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-emerald-200">
          You can still restore your data
        </p>
        <p className="text-xs text-emerald-100/80 mt-0.5">
          {timeLabel} before your archive is permanently purged.
        </p>
      </div>
      <Button
        type="button"
        onClick={handleRestore}
        disabled={isRestoring}
        data-testid="archive-restore-btn"
        className="bg-emerald-500 hover:bg-emerald-400 text-white shrink-0"
      >
        {isRestoring ? (
          <>
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Restoring…
          </>
        ) : (
          <>
            <Undo2 className="w-4 h-4 mr-1.5" /> Restore
          </>
        )}
      </Button>
    </Card>
  );
}
